<script setup lang="ts">
type BagProps = {
  buttonClass: string
}

const props = defineProps<BagProps>()
const { bagUrl } = useSiteUrl()
const basketsStore = useBasketsStore()

provide('location', LOCATIONS.header)

const handleHoverEvent = () => {
  pushEventToDataLayer('custom.ecommerce.floatingCart.view', {
    group: 'actions menu',
    type: 'hover',
    info: {
      location: LOCATIONS.header,
    },
  })
}
</script>

<template>
  <div class="bag" @mouseenter="handleHoverEvent">
    <Anchor class="bag__button" variant="none" :class="buttonClass" aria-label="My Bag" :url="bagUrl">
      <svg-icon name="mhj:bag" />
      <client-only>
        <span v-if="basketsStore.count > 0" class="bag__count">{{ basketsStore.count }}</span>
      </client-only>
    </Anchor>

    <client-only>
      <section v-if="basketsStore.count > 0" class="bag__summary">
        <div class="bag__scroll">
          <template v-for="basket in basketsStore.baskets" :key="basket.basketId">
            <template v-if="basket.productItems" class="my-bag-table">
              <ProductCardSummary
                v-for="item in basket.productItems"
                :key="item.itemId"
                :product="{
                  ...item,
                  productId: item.productId || '',
                  productName: item.productName || '',
                  price: item.price || 0,
                  image: item.image,
                  tieredPrice: item.productPrices,
                }"
              />
            </template>
          </template>
        </div>
        <div class="bag__bottom">
          <div class="bag__total">
            <div>{{ $t('shopping_bag.sub_total') }}</div>
            <CurrencyFormat :value="basketsStore.basket?.orderTotal" :decimal="0" />
          </div>
          <Anchor class="bag__checkout-button" variant="primary" appearance="Button" :url="bagUrl">
            {{ $t('shopping_bag.button.view_bag') }}
          </Anchor>
        </div>
      </section>
    </client-only>
  </div>
</template>

<style lang="postcss">
@import '~/assets/css/placeholders.css';

.bag {
  position: relative;

  .bag__button {
    position: relative;
  }

  .bag__count {
    position: absolute;
    right: 0;
    top: 50%;
    translate: 50% -50%;
    height: 1rem;
    width: 1rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: var(--text-disclaimer);
    letter-spacing: 0.01rem;
    color: var(--text-reverse);
    background-color: var(--surface-dark-10);
    border: 1px solid var(--surface-light-00);
    border-radius: var(--border-radius-full);
    line-height: 1;
    padding-left: 0.08rem;
  }

  .bag__summary {
    position: absolute;
    z-index: var(--z-header-bag);
    filter: drop-shadow(0 18px 10px rgba(0, 0, 0, 0.1)) drop-shadow(0 42px 18px rgba(0, 0, 0, 0.1));
    top: 3.1rem;
    right: 0;
    width: 28rem; /* 448px */
    background-color: var(--surface-light-00);
    overflow: hidden;
    max-height: 0;
    transition: all 0.4s allow-discrete;
  }

  .bag__total {
    @extend %heading-3;
    display: flex;
    justify-content: space-between;
    margin-bottom: var(--space-70);
  }

  .bag__scroll {
    display: flex;
    flex-direction: column;
    gap: var(--space-50);
    padding-inline: var(--space-50);
    padding-top: var(--space-50);
    max-height: 20.7rem;
    overflow: auto;
  }

  .bag__bottom {
    border-top: 1px solid var(--border-neutral-20);
    padding-top: var(--space-50);
    margin: var(--space-50);
  }

  .bag__checkout-button {
    width: 100%;
  }

  @media (--desktop) {
    &:has(.bag__button:hover) .bag__summary,
    .bag__summary:hover {
      max-height: 32.25rem; /* 516px */
    }
  }
}
</style>
